/**
 * /admin/meta-approvals — Meta 자산 승인 큐 어드민 화면
 *
 * 고객이 `/setup/meta-ads` 또는 `/setup/meta-catalog` 에서 자산 ID 를 입력했을 때
 * Meta API 즉시 검증이 실패하면 `meta_asset_approval_request` 테이블에 pending 요청으로
 * 쌓인다. 운영이 Meta Business Manager 에서 System User 에 자산을 할당한 뒤
 * 이 화면에서 "승인" 을 누르면 서버가 다시 검증해 성공 시 Shop 컬럼에 반영한다.
 *
 * 라우트 파일은 URL search param(tab/offset) 만 파싱하고 실제 UI 는
 * `MetaApprovalQueueSection` 에 위임한다. 부모 `/admin` 라우트가 인증 가드와
 * `AdminLayout` 을 이미 담당하므로 여기서는 아무것도 할 필요가 없다.
 */
import { createFileRoute } from '@tanstack/react-router'
import { MetaApprovalQueueSection } from '@/features/admin/MetaApprovalQueueSection'

// URL search param 타입 — 탭 선택과 페이지네이션 상태를 URL 에 보존한다.
// 새로고침해도 위치가 유지되고, 히스토리 뒤로가기로도 이전 탭으로 돌아갈 수 있다.
interface MetaApprovalsSearchParams {
  tab?: 'pending' | 'history'
  offset?: number
}

export const Route = createFileRoute('/admin/meta-approvals')({
  component: AdminMetaApprovalsPage,
  validateSearch: (search: Record<string, unknown>): MetaApprovalsSearchParams => {
    // tab 은 pending/history 둘 중 하나만 허용, 그 외는 undefined 로 취급해
    // 섹션 내부에서 기본값(pending)을 쓰도록 한다.
    const rawTab = search.tab
    const tab =
      rawTab === 'pending' || rawTab === 'history' ? rawTab : undefined

    // offset 은 음수/소수/숫자 아님 방어 — 0 미만은 0 으로 보정
    const rawOffset = search.offset
    const offset =
      typeof rawOffset === 'number' && Number.isFinite(rawOffset) && rawOffset >= 0
        ? Math.floor(rawOffset)
        : undefined

    return { tab, offset }
  },
})

function AdminMetaApprovalsPage() {
  const { tab, offset } = Route.useSearch()
  return <MetaApprovalQueueSection tab={tab} offset={offset} />
}
